import { useState, useCallback, useEffect } from 'react';
import { Translate as t } from '../config/Translate';
import { S3Upload } from '../components/common/FileUpload';

// IMP-107 (AC-1): 배경 이미지 업로드 책임 분리 훅.
// 파일 선택 검증(MIME / magic bytes / size) → ObjectURL 미리보기 → SHA-256 → S3 업로드.
// VoucherTemplateEditor 에서 이전. 동작 보존.

// 허용 MIME — SVG는 XSS 벡터이므로 명시적 거부 (스펙 AC-5)
const ALLOWED_MIME = ['image/png', 'image/jpeg'];
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

// PR #183 R6: MIME 스푸핑 defense-in-depth. file.type 은 브라우저가 확장자로 채우므로 변조 가능 →
// 실제 파일 첫 바이트로 재판정. PNG: 89 50 4E 47 0D 0A 1A 0A / JPEG: FF D8 FF
const readMagicBytes = (file, byteCount = 8) => new Promise((resolve, reject) => {
    try {
        const reader = new FileReader();
        reader.onload = () => resolve(new Uint8Array(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(file.slice(0, byteCount));
    } catch (err) {
        reject(err);
    }
});

const matchMagic = (bytes, signature) => signature.every((b, i) => bytes[i] === b);

const detectImageType = async (file) => {
    const bytes = await readMagicBytes(file);
    if (matchMagic(bytes, [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A])) return 'image/png';
    if (matchMagic(bytes, [0xFF, 0xD8, 0xFF])) return 'image/jpeg';
    return null;
};

// 클라이언트 SHA-256 — "UI 비교용" 값. 무결성 보장은 서버 재계산에 의존(Phase 2). 실패 시 null.
const computeSha256Hex = async (file) => {
    if (!file || typeof crypto === 'undefined' || !crypto.subtle) return null;
    try {
        const buf = await file.arrayBuffer();
        const hash = await crypto.subtle.digest('SHA-256', buf);
        return Array.from(new Uint8Array(hash))
            .map((b) => b.toString(16).padStart(2, '0'))
            .join('');
    } catch {
        return null;
    }
};

export const useImageUpload = () => {
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [checksum, setChecksum] = useState(null);
    const [error, setError] = useState(null);

    // 파일 선택 검증 → file/previewUrl set. 성공 시 선택된 File 반환, 실패 시 null(error set).
    const handleFileChange = useCallback(async (e) => {
        setError(null);
        const picked = e.target.files?.[0];
        if (!picked) return null;
        if (!ALLOWED_MIME.includes(picked.type)) {
            setError(t('io.message.error.onlyPngOrJpegAllowed'));
            return null;
        }
        if (picked.size > MAX_FILE_SIZE_BYTES) {
            setError(t('io.message.error.fileTooLargeMax5Mb'));
            return null;
        }
        // Magic bytes 재검증 — MIME 스푸핑 차단 (PNG/JPEG 헤더만 통과)
        try {
            const detected = await detectImageType(picked);
            if (!detected || !ALLOWED_MIME.includes(detected)) {
                setError(t('product.message.invalidImageType'));
                return null;
            }
        } catch (err) {
            console.warn('[useImageUpload] magic bytes read failed', err);
            setError(t('product.message.unableToVerifyFile'));
            return null;
        }
        setFile(picked);
        setPreviewUrl(URL.createObjectURL(picked));
        return picked;
    }, []);

    // 선택된 파일을 SHA-256 계산 + S3 업로드. { url, checksum } 반환, 실패 시 url=null.
    // 파일 미선택 시 null 반환(호출부는 업로드 스킵 분기).
    const uploadToS3 = useCallback(async (companyId) => {
        if (!file) return null;
        const sum = await computeSha256Hex(file);
        if (!sum) {
            // crypto.subtle 부재/실패 — checksum 은 "UI 비교용" 이므로 저장은 계속하되 흔적을 남긴다.
            console.warn('[useImageUpload] SHA-256 unavailable, uploading without checksum');
        }
        setChecksum(sum);
        // S3Upload 는 디렉토리 접두어를 받고 내부에서 파일명을 생성하므로 '/' 로 끝나는 dir 전달.
        const dirPath = `voucher-template/${companyId}/`;
        const s3Result = await S3Upload(dirPath, file);
        // R2 경로는 directLink, Ali OSS 경로는 url 을 반환. Location 은 구 AWS SDK 호환용.
        const url = s3Result?.directLink || s3Result?.url || s3Result?.Location || null;
        return { url, checksum: sum };
    }, [file]);

    const reset = useCallback(() => {
        setPreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
        });
        setFile(null);
        setChecksum(null);
        setError(null);
    }, []);

    // ObjectURL 정리 — previewUrl 변경/언마운트 시 직전 URL revoke.
    useEffect(() => () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
    }, [previewUrl]);

    return { file, previewUrl, checksum, error, handleFileChange, uploadToS3, reset };
};

export default useImageUpload;
